import { DEFAULT_LOCALE, type Locale } from './i18n.ts'

/**
 * Nepali for the messages that come out of zod.
 *
 * These are the one family of user-facing strings that is not keyed, and the reason
 * is worth stating: they are written *inside* the schemas, beside the rule they
 * describe, which is what stops a `min(1)` and its "Enter a name." drifting apart.
 * Keying them would move the sentence away from the constraint and buy nothing —
 * both sides of the wire already import the same schema object.
 *
 * So the English is the key. It is matched exactly, and a miss falls through to the
 * English, which is the same behaviour as any other untranslated string rather than
 * a blank. `validation-messages.test.ts` walks the schemas and fails if a message
 * exists that this table has never heard of, so a miss is a build failure rather
 * than something a Nepali reader discovers.
 */
const MESSAGES: Record<string, string> = {
  // Custom messages, in schema order
  'Add at least one item.': 'कम्तीमा एउटा सामान हाल्नुहोस्।',
  'Add at least one price row.': 'कम्तीमा एउटा रेट पङ्क्ति हाल्नुहोस्।',
  'Amount is too large': 'रकम धेरै ठुलो भयो',
  'A quote needs at least one item.': 'रेटमा कम्तीमा एउटा सामान चाहिन्छ।',
  'Choose a password different from your current one.': 'हालको भन्दा फरक पासवर्ड रोज्नुहोस्।',
  'Enter a 10-digit mobile number starting with 9.': '9 बाट सुरु हुने 10 अङ्कको मोबाइल नम्बर हाल्नुहोस्।',
  'Enter a category name.': 'क्याटेगोरीको नाम हाल्नुहोस्।',
  'Enter a name.': 'नाम हाल्नुहोस्।',
  'Enter a quantity.': 'परिमाण हाल्नुहोस्।',
  'Enter how much arrived.': 'कति आयो हाल्नुहोस्।',
  'Enter the address.': 'ठेगाना हाल्नुहोस्।',
  'Enter the business name.': 'व्यवसायको नाम हाल्नुहोस्।',
  'Enter the city.': 'सहर हाल्नुहोस्।',
  'Enter the item name.': 'सामानको नाम हाल्नुहोस्।',
  'Enter your current password.': 'हालको पासवर्ड हाल्नुहोस्।',
  'Enter your password.': 'पासवर्ड हाल्नुहोस्।',
  'Enter your rate.': 'आफ्नो रेट हाल्नुहोस्।',
  'Give both the pack size and its unit, or neither.':
    'प्याकको साइज र एकाइ दुवै हाल्नुहोस्, नत्र दुवै छोड्नुहोस्।',
  'Give it a name, like "Site" or "Yard".': 'नाम दिनुहोस्, जस्तै "साइट" वा "गोदाम"।',
  'Give it a subject.': 'विषय दिनुहोस्।',
  'Give the arrangement a name.': 'मिलापत्रलाई नाम दिनुहोस्।',
  'Give the list a name.': 'लिस्टलाई नाम दिनुहोस्।',
  'Mobile number is required.': 'मोबाइल नम्बर चाहिन्छ।',
  'One item can have 12 price rows at most.': 'एउटा सामानमा बढीमा 12 रेट पङ्क्ति हुन सक्छ।',
  'Only the last row can be open-ended.': 'अन्तिम पङ्क्ति मात्र खुला छोड्न मिल्छ।',
  'PAN/VAT is 9 digits.': 'PAN/VAT 9 अङ्कको हुन्छ।',
  'Phone number is required.': 'फोन नम्बर चाहिन्छ।',
  'Pick an item.': 'सामान रोज्नुहोस्।',
  'Pick a shop.': 'पसल रोज्नुहोस्।',
  'Pick at least one shop.': 'कम्तीमा एउटा पसल रोज्नुहोस्।',
  'Pick exactly one customer.': 'ठिक एउटा ग्राहक रोज्नुहोस्।',
  'Pick exactly one customer or shop.': 'ठिक एउटा ग्राहक वा पसल रोज्नुहोस्।',
  'Pick the item this matches.': 'यो कुन सामानसँग मिल्छ रोज्नुहोस्।',
  'Pick the shop order this comes from.': 'यो कुन पसल अर्डरबाट आएको हो रोज्नुहोस्।',
  'Price goes up for a larger quantity. Is that intended?': 'बढी परिमाणमा रेट बढेको छ। यही ठिक हो?',
  'Say what happened.': 'के भयो लेख्नुहोस्।',
  'Say why, so the shop can fix it.': 'किन भन्ने लेख्नुहोस्, पसलले मिलाउन सकोस्।',
  'That is more than a standing discount should be.': 'स्थायी छुट यति हुनु हुँदैन।',
  'The last row must be open-ended, so any large order still has a price.':
    'अन्तिम पङ्क्ति खुला हुनुपर्छ, ठुलो अर्डरको पनि रेट निस्कियोस्।',
  'The open-ended row must come last.': 'खुला पङ्क्ति सबैभन्दा तल हुनुपर्छ।',
  'The two passwords do not match.': 'दुई पासवर्ड मिलेनन्।',
  'Unknown unit': 'नचिनिएको एकाइ',
  'Write a message or attach a file.': 'मेसेज लेख्नुहोस् वा फाइल जोड्नुहोस्।',
}

/**
 * zod's own wording, for the constraints no schema bothered to name. Matched on the
 * shape zod produces rather than the whole string, because the numbers in them vary.
 */
const PATTERNS: [RegExp, (m: RegExpMatchArray) => string][] = [
  [/^Invalid input: expected/i, () => 'हालेको कुरा मिलेन।'],
  [/^Too small: expected .* >=?(\d+) character/i, (m) => `कम्तीमा ${m[1]} अक्षर चाहिन्छ।`],
  [/^Too big: expected .* <=?(\d+) character/i, (m) => `बढीमा ${m[1]} अक्षर हुन सक्छ।`],
  [/^Too small: expected .* >=?(\d+)/i, (m) => `कम्तीमा ${m[1]} हुनुपर्छ।`],
  [/^Too big: expected .* <=?(\d+)/i, (m) => `बढीमा ${m[1]} हुन सक्छ।`],
  [/^Invalid option/i, () => 'रोजेको विकल्प मिलेन।'],
  [/^Invalid email/i, () => 'इमेल ठेगाना मिलेन।'],
  [/^Invalid/i, () => 'हालेको कुरा मिलेन।'],
  [/^Required/i, () => 'यो चाहिन्छ।'],
]

/** Every English message this table knows, for the test that guards it. */
export const KNOWN_VALIDATION_MESSAGES: readonly string[] = Object.keys(MESSAGES)

export function localizeValidationMessage(
  message: string,
  locale: Locale = DEFAULT_LOCALE,
): string {
  if (locale === DEFAULT_LOCALE) return message
  const exact = MESSAGES[message]
  if (exact) return exact
  for (const [pattern, render] of PATTERNS) {
    const match = message.match(pattern)
    if (match) return render(match)
  }
  return message
}
